import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import { useScrollAnimation, getStaggerProps, animationClasses } from '@/hooks/useScrollAnimation';

const DOMAIN = 'https://finline.sonbook.net';
const FAQ_PAGE_URL = `${DOMAIN}/faq`;

interface FAQEntry {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  questions: FAQEntry[];
}

const faqCategories: FAQCategory[] = [
  {
    title: '關於財經錢線',
    questions: [
      {
        question: '財經錢線是什麼品牌？',
        answer: '財經錢線以商業理財為主要出版方向，並涵蓋投資心理學與以古諷今類內容，透過出版作品呈現市場、人性、歷史與現實決策等議題。\n\n財經錢線為崧博出版合作經銷品牌之一，品牌網站提供正式書籍介紹、書目查詢、合作購書通路及常見問題資訊。',
      },
      {
        question: '財經錢線與崧博出版是什麼關係？',
        answer: '財經錢線為崧博出版合作經銷品牌之一。讀者可透過財經錢線品牌網站瀏覽相關出版內容，並前往崧博出版書籍資料庫查詢正式書目。',
      },
      {
        question: '財經錢線網站提供哪些資訊？',
        answer: '網站主要提供品牌介紹、商業理財、投資心理與以古諷今類出版內容、正式書目、合作購書通路及常見問題資訊。',
      },
    ],
  },
  {
    title: '出版內容與閱讀方向',
    questions: [
      {
        question: '財經錢線主要出版哪些內容？',
        answer: '財經錢線以商業理財為主要出版方向，並涵蓋投資心理學與以古諷今類內容。\n\n實際作品主題與分類，仍應以品牌網站現有書籍及正式書目資料為準。',
      },
      {
        question: '什麼是財經錢線的商業理財內容？',
        answer: '商業理財內容主要涵蓋商業運作、財務觀念、經濟現象、管理思維與個人決策等主題，協助讀者理解金錢、組織與市場之間的關係。',
      },
      {
        question: '什麼是投資心理學類內容？',
        answer: '投資心理學類內容關注人在投資與決策過程中的情緒、認知偏誤、群體行為、風險判斷與資訊影響。\n\n這類出版內容主要協助讀者理解人性如何影響判斷，不代表提供個別投資建議。',
      },
      {
        question: '什麼是以古諷今類內容？',
        answer: '以古諷今類內容透過歷史人物、政治事件、制度變化與社會經驗，對照當代商業、治理、人性與現實問題。',
      },
      {
        question: '財經錢線適合哪些讀者？',
        answer: '適合對商業理財、投資心理、人性決策、歷史借鑑與現實議題感興趣，並希望透過正式出版內容建立多角度理解的讀者。',
      },
    ],
  },
  {
    title: '書籍與正式書目',
    questions: [
      {
        question: '如何查詢財經錢線的出版書籍？',
        answer: '可先瀏覽財經錢線品牌網站目前公開的出版內容。如需確認完整書名、作者、ISBN、出版日期及其他正式書目資訊，請前往崧博出版書籍資料庫查詢。',
      },
      {
        question: '網站上的書籍資料以哪裡為準？',
        answer: '完整書名、作者、ISBN、出版日期及其他正式出版資訊，應以崧博出版書籍資料庫與正式公開書目為準。',
      },
      {
        question: '網站上的內容簡介是完整書籍內容嗎？',
        answer: '不是。網站上的內容簡介主要協助讀者了解作品主題與閱讀方向，完整內容仍以正式出版品為準。',
      },
      {
        question: '如何使用ISBN查找書籍？',
        answer: 'ISBN可用於辨識特定出版品。若書名較長、同名作品較多或搜尋結果不明確，建議使用ISBN查詢。',
      },
      {
        question: '2026最新發行書籍如何判定？',
        answer: '2026最新發行書籍只顯示正式出版年份為2026年，且已有確認書目資料的作品，並依正式出版日期由新到舊排列。',
      },
    ],
  },
  {
    title: '購書通路、價格與庫存',
    questions: [
      {
        question: '在哪裡可以購買財經錢線的書？',
        answer: '可透過網站合作購書通路頁所列的正式平台查找。不同作品實際上架的平台可能不同，請以各通路頁面顯示為準。',
      },
      {
        question: '所有書籍都會在全部合作通路上架嗎？',
        answer: '不一定。不同作品的上架平台、版本與供應狀態可能不同，實際資訊請以各合作通路頁面為準。',
      },
      {
        question: '為什麼在某個平台找不到書籍？',
        answer: '建議先至崧博出版書籍資料庫確認正式書名、作者與ISBN，再前往其他網站已建立的合作購書通路查找。',
      },
      {
        question: '網站顯示的價格是最終售價嗎？',
        answer: '不是。書籍價格、折扣及促銷活動可能由各合作通路調整，實際售價請以通路頁面與結帳資訊為準。',
      },
      {
        question: '網站可以查詢即時庫存嗎？',
        answer: '財經錢線品牌網站不保證提供各通路即時庫存。實際庫存、配送及到貨資訊，請以各合作通路頁面為準。',
      },
      {
        question: '缺貨書籍一定會補貨嗎？',
        answer: '不一定。補貨、再版與重新上架狀態，需依實際出版及通路情況而定。',
      },
    ],
  },
  {
    title: '財經內容與使用限制',
    questions: [
      {
        question: '財經錢線網站提供投資建議嗎？',
        answer: '不提供。財經錢線品牌網站主要用於介紹正式出版作品、書目資料與合作購書通路，不構成投資、證券、金融、稅務、法律或其他個人化專業建議。',
      },
      {
        question: '閱讀財經錢線書籍是否能保證投資獲利？',
        answer: '不能。任何閱讀內容都不能保證投資報酬、避免損失或達成特定財務成果。讀者應依自身情況獨立判斷，必要時尋求合格專業人士協助。',
      },
      {
        question: '網站會推薦股票或金融商品嗎？',
        answer: '品牌網站不應提供個股、基金、期貨、外匯、加密資產或其他金融商品的買賣建議。',
      },
      {
        question: '書籍中的觀點是否代表即時市場資訊？',
        answer: '不一定。書籍內容反映作者及出版時的研究、觀點與資料背景，不應直接視為即時市場資訊或交易依據。',
      },
      {
        question: '投資心理學內容是否等同投資策略？',
        answer: '不等同。投資心理學主要探討情緒、認知偏誤、群體行為與風險判斷，不代表提供特定金融商品的操作策略。',
      },
    ],
  },
  {
    title: '品牌經銷與合作',
    questions: [
      {
        question: '如何洽詢財經錢線品牌合作？',
        answer: '如有財經錢線品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過網站現有的官方聯絡方式提出詢問。\n\n品牌經銷與合作方案詳情請直接聯繫洽詢。',
      },
      {
        question: '財經錢線是否提供投資顧問或財務規劃服務？',
        answer: '財經錢線品牌網站不宣稱提供投資顧問、個人化財務規劃、資產配置、個股推薦或投資代操服務。',
      },
      {
        question: '財經錢線是否提供出版代辦服務？',
        answer: '財經錢線品牌網站目前不宣稱提供出版代辦、排版代工、印刷代工、發行代辦或上架代辦服務。',
      },
      {
        question: '合作洽詢是否保證出版、上架或銷售？',
        answer: '合作洽詢不代表保證出版、上架、曝光或銷售成果，實際合作內容仍需依雙方確認為準。',
      },
    ],
  },
];

function buildFAQPageLD(): Record<string, unknown> {
  const mainEntity = faqCategories.flatMap((cat) =>
    cat.questions.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer.replace(/\n\n/g, ' ').replace(/\n/g, ' '),
      },
    })),
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${FAQ_PAGE_URL}#webpage`,
    url: FAQ_PAGE_URL,
    name: '財經錢線常見問題',
    inLanguage: 'zh-TW',
    isPartOf: {
      '@id': `${DOMAIN}/#website`,
    },
    mainEntity,
  };
}

function buildBreadcrumbLD(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${FAQ_PAGE_URL}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首頁',
        item: `${DOMAIN}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '常見問題',
        item: FAQ_PAGE_URL,
      },
    ],
  };
}

const OPENING_SUMMARY_PARAGRAPH_1 =
  '本頁整理財經錢線常見問題，內容包含品牌與出版方向、正式書目查詢、合作購書通路、價格與庫存，以及品牌經銷與合作洽詢方式。';

const OPENING_SUMMARY_PARAGRAPH_2 =
  '財經錢線以商業理財為主要出版方向，並涵蓋投資心理學與以古諷今類內容，透過正式出版作品呈現市場、人性、歷史與現實決策等議題。';

const OPENING_SUMMARY_PARAGRAPH_3 =
  '財經錢線為崧博出版合作經銷品牌之一。完整書名、作者、ISBN與出版日期請以正式書目資料為準；價格、版本、庫存及購買方式則以各合作通路頁面為準。';

const GEO_PARAGRAPH_1 =
  '財經錢線常見問題頁整理品牌定位、商業理財、投資心理學、以古諷今類出版內容、正式書目查詢、合作購書通路、價格與庫存，以及品牌合作資訊。';

const GEO_PARAGRAPH_2 =
  '財經錢線為崧博出版合作經銷品牌之一。完整書名、作者、ISBN與出版日期請以崧博出版書籍資料庫為準；價格、版本、庫存及購買方式則以各合作通路頁面為準。';

const GEO_PARAGRAPH_3 =
  '網站內容主要用於介紹正式出版作品，不構成投資、證券、金融、稅務、法律或其他個人化專業建議。';

export default function FAQ() {
  const [openKey, setOpenKey] = useState<string | null>('0-0');
  const heroRef = useScrollAnimation({ threshold: 0.12 });
  const mainRef = useScrollAnimation();

  const toggleFAQ = useCallback((key: string) => {
    setOpenKey((prev) => (prev === key ? null : key));
  }, []);

  const handleKeyDown = useCallback(
    (key: string, e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleFAQ(key);
      }
    },
    [toggleFAQ],
  );

  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.id = 'faq-page-ld';
    faqScript.text = JSON.stringify(buildFAQPageLD());
    document.head.appendChild(faqScript);
    scripts.push(faqScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'faq-breadcrumb-ld';
    breadcrumbScript.text = JSON.stringify(buildBreadcrumbLD());
    document.head.appendChild(breadcrumbScript);
    scripts.push(breadcrumbScript);

    return () => {
      scripts.forEach((s) => {
        const el = document.getElementById(s.id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <>
      <PageMeta
        title="財經錢線常見問題｜商業理財書籍、書目與購書資訊"
        description="查看財經錢線常見問題，了解商業理財、投資心理與以古諷今類書籍的正式書目查詢、合作購書通路、價格庫存及品牌合作資訊。"
        canonical="/faq"
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section ref={heroRef.ref} className="relative min-h-[100svh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <video
              src="https://public.readdy.ai/ai/video_res/01a0ec97-173f-7a53-b04f-65634b801560.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-900/55 via-primary-800/35 to-primary-900/65"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-24 md:py-32">
            <div className={`max-w-4xl mx-auto text-center ${animationClasses.base} ${heroRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
              <span className="inline-block text-sm md:text-base font-semibold text-accent-200 tracking-wider uppercase mb-4" style={{ transitionDelay: '0ms', textShadow: '0 2px 10px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)' }}>
                常見問答
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-5 tracking-tight" style={{ transitionDelay: '100ms', textShadow: '0 2px 12px rgba(0,0,0,0.5), 0 4px 24px rgba(0,0,0,0.35)' }}>
                財經錢線常見問題
              </h1>
              <p className="text-background-50 text-base md:text-lg max-w-2xl mx-auto" style={{ transitionDelay: '180ms', textShadow: '0 2px 10px rgba(0,0,0,0.55), 0 1px 4px rgba(0,0,0,0.35)' }}>
                {OPENING_SUMMARY_PARAGRAPH_1}
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section ref={mainRef.ref} className="bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">

              {/* GEO Summary */}
              <div className={`mb-10 space-y-3 ${animationClasses.base} ${mainRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {GEO_PARAGRAPH_1}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {GEO_PARAGRAPH_2}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {GEO_PARAGRAPH_3}
                </p>
              </div>

              {/* Opening Summary paragraphs 2 & 3 */}
              <div className="mb-10 space-y-3">
                <p {...getStaggerProps(mainRef.isVisible, 0, 200, 'fadeUp')} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {OPENING_SUMMARY_PARAGRAPH_2}
                </p>
                <p {...getStaggerProps(mainRef.isVisible, 1, 260, 'fadeUp')} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {OPENING_SUMMARY_PARAGRAPH_3}
                </p>
              </div>

              {/* FAQ Categories — each category gets staggered entrance */}
              {faqCategories.map((category, catIdx) => (
                <div
                  key={catIdx}
                  {...getStaggerProps(mainRef.isVisible, catIdx, 300, 'fadeUp')}
                  className="mb-10"
                >
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-5">
                    {category.title}
                  </h2>
                  <div className="space-y-3">
                    {category.questions.map((faq, qIdx) => {
                      const key = `${catIdx}-${qIdx}`;
                      const isOpen = openKey === key;
                      return (
                        <div
                          key={key}
                          className="rounded-xl border border-background-200/70 overflow-hidden"
                        >
                          <button
                            id={`faq-question-${key}`}
                            onClick={() => toggleFAQ(key)}
                            onKeyDown={(e) => handleKeyDown(key, e)}
                            aria-expanded={isOpen}
                            aria-controls={`faq-answer-${key}`}
                            className="w-full flex items-center justify-between p-5 md:p-6 text-left bg-background-100 hover:bg-background-200/50 transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary-400"
                          >
                            <span className="font-medium text-foreground-800 text-sm md:text-base pr-4">
                              {faq.question}
                            </span>
                            <span className="w-6 h-6 flex items-center justify-center shrink-0">
                              <i
                                className={`ri-arrow-down-s-line text-foreground-400 text-lg transition-transform ${
                                  isOpen ? 'rotate-180' : ''
                                }`}
                              ></i>
                            </span>
                          </button>
                          {isOpen && (
                            <div
                              id={`faq-answer-${key}`}
                              role="region"
                              aria-labelledby={`faq-question-${key}`}
                              className="p-5 md:p-6 pt-0 text-sm md:text-base text-foreground-600 leading-relaxed text-left bg-background-100 whitespace-pre-line"
                            >
                              {faq.answer}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}